/**
 * A utility class for managing URL parameters in the browser without page reloads.
 * Works with both vanilla TypeScript/JavaScript and React applications.
 */
export class UrlStateManager {
  private url: URL;
  private batchUpdateTimeout: NodeJS.Timeout | null = null;
  private pendingUpdates = false;

  constructor() {
    this.url = new URL(window.location.href);
  }

  /**
   * Refresh the internal URL snapshot from the current window location.
   * This prevents inconsistencies when the host environment (e.g., Storybook iframe)
   * mutates the URL outside of this manager.
   */
  private refreshFromWindow(): void {
    try {
      this.url = new URL(window.location.href);
    } catch {
      // No-op: keep previous snapshot if URL parsing fails
    }
  }

  /**
   * Gets the value of a URL parameter
   * @param key - The parameter key to retrieve
   * @returns The parameter value or null if not found
   */
  public getParam(key: string): string | null {
    this.refreshFromWindow();
    return this.url.searchParams.get(key);
  }

  /**
   * Sets a parameter only if it already exists in the URL
   * @param key - The parameter key to set
   * @param value - The value to set
   */
  public setParam(key: string, value: string): void {
    this.refreshFromWindow();
    if (this.url.searchParams.has(key)) {
      this.url.searchParams.set(key, value);
      this.scheduleUpdate();
    }
  }

  /**
   * Sets or updates a parameter regardless of its existence
   * @param key - The parameter key to set or update
   * @param value - The value to set
   */
  public addOrUpdateParam(key: string, value: string): void {
    this.refreshFromWindow();
    this.url.searchParams.set(key, value);
    this.scheduleUpdate();
  }

  /**
   * Removes a parameter if it exists
   * @param key - The parameter key to remove
   */
  public removeParam(key: string): void {
    this.refreshFromWindow();
    if (this.url.searchParams.has(key)) {
      this.url.searchParams.delete(key);
      this.scheduleUpdate();
    }
  }

  /**
   * Removes all URL parameters
   */
  public removeAllParams(): void {
    this.refreshFromWindow();
    this.url.search = '';
    this.scheduleUpdate();
  }

  /**
   * Gets all URL parameters as a key-value object
   * @returns An object containing all URL parameters
   */
  public getAllParams(): Record<string, string> {
    this.refreshFromWindow();
    const params: Record<string, string> = {};
    this.url.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Schedules a URL update to batch multiple changes together
   */
  private scheduleUpdate(): void {
    if (this.batchUpdateTimeout) {
      clearTimeout(this.batchUpdateTimeout);
    }

    this.pendingUpdates = true;

    this.batchUpdateTimeout = setTimeout(() => {
      if (this.pendingUpdates) {
        this.updateUrl();
        this.pendingUpdates = false;
      }
    }, 10); // Small delay to batch rapid changes
  }

  /**
   * Updates the browser URL without reloading the page
   */
  public updateUrl(): void {
    try {
      // Check if we're in an Optimizely environment
      const isOptimizely =
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('optimizely') ||
          window.location.hostname.includes('episerver') ||
          window.location.hostname.includes('epi'));

      if (isOptimizely) {
        // In Optimizely, be more careful with URL updates
        const currentUrl = window.location.href;
        const newUrl = this.url.toString();

        // Only update if the URL actually changed
        if (currentUrl !== newUrl) {
          window.history.replaceState({}, '', newUrl);
          // Update the internal URL object to reflect any changes made by other code
          this.url = new URL(window.location.href);
        }
      } else {
        // Standard behavior for non-Optimizely environments
        window.history.replaceState({}, '', this.url.toString());
        // Update the internal URL object to reflect any changes made by other code
        this.url = new URL(window.location.href);
      }
    } catch (error) {
      console.error('Error updating URL:', error);
    }
  }
}
